import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Download, Search as SearchIcon, Users } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import { AdminTabs } from "@/components/AdminTabs";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

const ROLE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "customer", label: "By customers" },
  { key: "collector", label: "By collectors" },
];

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid={testid}>
    <div className="text-2xl font-heading text-[#1E3F33]">{value}</div>
    <div className="text-xs uppercase tracking-wider text-[#5C6B64] mt-1">{label}</div>
  </div>
);

export default function AdminReferrals() {
  const [data, setData] = useState({ items: [], totals: { total: 0, from_collectors: 0, from_customers: 0 } });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (role !== "all") params.role = role;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/admin/referrals", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range]);

  const exportCSV = () => {
    const rows = data.items.map((r) => ({
      referrer: r.referrer_name || r.referrer_username,
      referrer_role: r.referrer_role,
      referred: r.referred_name || r.referred_username,
      referred_role: r.referred_role,
      code: r.code,
      joined_at: r.created_at,
    }));
    if (!rows.length) return toast.info("No referrals to export.");
    downloadCSV("referrals.csv", rows);
  };

  return (
    <div className="container-x py-12" data-testid="admin-referrals">
      <AdminTabs />
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Marketing</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
        <Share2 className="w-6 h-6 text-[#2D6A4F]" /> Referrals
      </h1>
      <p className="text-[#5C6B64] mt-2 max-w-2xl">
        Every collector and customer gets a personal invite link. This is who they've brought to the platform.
      </p>

      <div className="grid grid-cols-3 gap-4 mt-6 max-w-xl">
        <Stat label="Total referrals" value={data.totals.total} testid="stat-total" />
        <Stat label="From collectors" value={data.totals.from_collectors} testid="stat-collectors" />
        <Stat label="From customers" value={data.totals.from_customers} testid="stat-customers" />
      </div>

      <div className="mt-6 space-y-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 max-w-md">
          <input
            data-testid="referrals-search-input"
            className="input-soft"
            placeholder="Search by name, username, or code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" data-testid="referrals-search-button" className="btn-primary py-2.5 px-4 inline-flex items-center gap-2">
            <SearchIcon className="w-4 h-4" /> Search
          </button>
        </form>

        <div className="flex flex-col gap-3 rounded-xl border border-[#1E3F33]/10 bg-[#F7F6F2] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Referred by</span>
            <FilterPills options={ROLE_OPTIONS} value={role} onChange={setRole} testidPrefix="referrals-role" />
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter onChange={(start, end) => setRange({ start, end })} />
            <button type="button" data-testid="referrals-export" onClick={exportCSV} className="btn-secondary text-xs py-2 inline-flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="mt-10 flex flex-col items-center text-center text-[#8A9A86] py-16 border border-dashed border-[#1E3F33]/15 rounded-2xl">
          <Users className="w-8 h-8 mb-3 text-[#8A9A86]" />
          <p className="text-sm">No referrals yet. Once members share their invite links, sign-ups will appear here.</p>
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-[#F0EFEA] text-[#1E3F33] text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium">Referrer</th>
                <th className="px-4 py-2.5 font-medium">Invited</th>
                <th className="px-4 py-2.5 font-medium">Code</th>
                <th className="px-4 py-2.5 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} className="border-t border-[#1E3F33]/8" data-testid={`referral-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-[#1E3F33]">{r.referrer_name || r.referrer_username}</div>
                    <div className="text-xs text-[#8A9A86] capitalize">{r.referrer_role}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-[#1E3F33]">{r.referred_name || r.referred_username}</div>
                    <div className="text-xs text-[#8A9A86] capitalize">{r.referred_role}</div>
                  </td>
                  <td className="px-4 py-2.5"><span className="text-xs font-mono px-2 py-0.5 rounded bg-[#1E3F33]/8 text-[#1E3F33]">{r.code}</span></td>
                  <td className="px-4 py-2.5 text-[#5C6B64]">{fmt(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
